/**
 * Canonical admission gate code taxonomy shared between Tangrowth (which produces findings) and
 * the Supabase edge functions (which store and read them). Codes follow the flat
 * `<namespace>.<attribute>` convention used throughout the codebase. The nested object is an
 * ergonomics layer over that flat string — grouping by namespace at call sites.
 *
 * A rejection finding is a verdict, not an error — do NOT add these to ERROR_CODES.
 * Covers both rejections (decisive gate findings) and escalations (gate could not decide).
 * The `code` set is the only shared surface; `message`/`details` stay free-form.
 */
export const ADMISSION_CODES = {
  COVERAGE: {
    MISSING_ANNOTATION_SECTIONS: "coverage.missing_annotation_sections",
    MISSING_JUDGMENT_SECTIONS: "coverage.missing_judgment_sections",
  },
  COMPATIBILITY: {
    LANGUAGE_NOT_SUPPORTED: "compatibility.language_not_supported",
    MULTIPLE_STUDY_TYPES: "compatibility.multiple_study_types",
    UNCLEAR: "compatibility.unclear",
  },
  EVIDENCE: {
    NOT_PRIMARY: "evidence.not_primary",
    NOT_HUMAN_HEALTH: "evidence.not_human_health",
    NOT_EMPIRICAL: "evidence.not_empirical",
    UNCLEAR: "evidence.unclear",
  },
} as const;

type LeafValues<T> = T extends string ? T : T extends object ? LeafValues<T[keyof T]> : never;

/** Machine-readable admission gate code, "<namespace>.<attribute>" convention. */
export type AdmissionCode = LeafValues<typeof ADMISSION_CODES>;

/** All valid admission codes as a flat tuple — use for runtime enum validation. */
export const ADMISSION_CODE_VALUES: [AdmissionCode, ...AdmissionCode[]] = Object.values(
  ADMISSION_CODES
).flatMap((g) => Object.values(g)) as [AdmissionCode, ...AdmissionCode[]];

/** A corpus rejection diagnostic — the code and message produced when the gate rejects or escalates. */
export interface RejectionFinding {
  code: AdmissionCode;
  message: string;
  details?: Record<string, unknown>;
}
